import { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { productsAPI, stockAPI } from '../lib/api';
import { formatNumber, getCategoryLabel } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Truck, 
  Warehouse, 
  Storefront, 
  ArrowRight, 
  Package, 
  ChartLine,
  Warning,
  CheckCircle,
  Info,
  TrendUp,
  TrendDown,
  MagnifyingGlass
} from '@phosphor-icons/react';

// Sapma seviyesi renkleri
const deviationColors = {
  normal: 'bg-green-100 text-green-700 border-green-200',
  attention: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  high: 'bg-orange-100 text-orange-700 border-orange-200',
  critical: 'bg-red-100 text-red-700 border-red-200',
  learning: 'bg-blue-100 text-blue-700 border-blue-200',  // Öğrenme dönemi
};

// Güven seviyesi etiketleri
const confidenceLabels = {
  high: { label: 'Yüksek Güven', color: 'text-green-600', bg: 'bg-green-100' },
  medium: { label: 'Orta Güven', color: 'text-yellow-600', bg: 'bg-yellow-100' },
  low: { label: 'Düşük Güven', color: 'text-orange-600', bg: 'bg-orange-100' },
  insufficient_data: { label: 'Yetersiz Veri', color: 'text-blue-600', bg: 'bg-blue-100' },
};

export default function Transfer() {
  const [products, setProducts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [lastTransfers, setLastTransfers] = useState({});
  const [loading, setLoading] = useState(true);
  const [transferring, setTransferring] = useState(false);
  const [activeTab, setActiveTab] = useState('transfer');
  
  // Akıllı analiz state
  const [transferPreview, setTransferPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [smartAnalysis, setSmartAnalysis] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [topTransfers, setTopTransfers] = useState([]);
  const [trends, setTrends] = useState(null);
  const [deviations, setDeviations] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [formData, setFormData] = useState({
    product_id: '',
    quantity: '',
    notes: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'analysis') {
      fetchSmartAnalysis();
    }
  }, [activeTab]);

  const fetchData = async () => {
    try {
      const [productsRes, transactionsRes, lastTransfersRes] = await Promise.all([
        productsAPI.getAll(),
        stockAPI.getTransactions({ transaction_type: 'transfer', limit: 30 }),
        stockAPI.getLastTransfers(),
      ]);
      setProducts(productsRes.data);
      setTransactions(transactionsRes.data);
      setLastTransfers(lastTransfersRes.data || {});
    } catch (error) {
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchSmartAnalysis = async () => {
    setAnalysisLoading(true);
    try {
      const [analysisRes, topRes, trendsRes, deviationsRes] = await Promise.all([
        stockAPI.getSmartAnalysis({ target_days: 7 }),
        stockAPI.getTopTransfers(30, 10),
        stockAPI.getTransferTrends(),
        stockAPI.getRecentDeviations({ days: 30, min_deviation_percent: 15 }),
      ]);
      setSmartAnalysis(analysisRes.data);
      setTopTransfers(topRes.data);
      setTrends(trendsRes.data);
      setDeviations(deviationsRes.data);
    } catch (error) {
      toast.error('Analiz verileri yüklenirken hata oluştu');
    } finally {
      setAnalysisLoading(false);
    }
  };

  // Ürün seçildiğinde veya miktar değiştiğinde preview al
  const fetchTransferPreview = useCallback(async (productId, quantity) => {
    if (!productId || !quantity || parseFloat(quantity) <= 0) {
      setTransferPreview(null);
      return;
    }
    
    setPreviewLoading(true);
    try {
      const res = await stockAPI.getTransferPreview({
        product_id: productId,
        quantity: parseFloat(quantity),
      });
      setTransferPreview(res.data);
    } catch (error) {
      console.error('Preview error:', error);
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  // Debounced preview fetch
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.product_id && formData.quantity) {
        fetchTransferPreview(formData.product_id, formData.quantity);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [formData.product_id, formData.quantity, fetchTransferPreview]);

  const handleTransfer = async (e) => {
    e.preventDefault();
    
    if (!formData.product_id || !formData.quantity) {
      toast.error('Lütfen ürün ve miktar seçin');
      return;
    }

    const product = products.find(p => p.id === formData.product_id);
    if (!product) return;

    const qty = parseFloat(formData.quantity);
    if (qty > product.warehouse_stock) {
      toast.error('Depoda yeterli stok yok');
      return;
    }

    // Yüksek sapma varsa ve not girilmemişse uyar
    if (transferPreview?.requires_explanation && !formData.notes?.trim()) {
      toast.error('Yüksek sapma tespit edildi. Lütfen sevkiyat nedenini not olarak belirtin.');
      return;
    }

    setTransferring(true);
    try {
      await stockAPI.transfer({
        product_id: formData.product_id,
        quantity: qty,
        notes: formData.notes || undefined,
      });
      
      toast.success('Sevkiyat tamamlandı');
      setFormData({ product_id: '', quantity: '', notes: '' });
      setTransferPreview(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sevkiyat başarısız');
    } finally {
      setTransferring(false);
    }
  };

  const selectedProduct = products.find(p => p.id === formData.product_id);
  const warehouseProducts = products.filter(p => p.warehouse_stock > 0);

  // Akıllı analiz filtreleme
  const filteredAnalysis = smartAnalysis?.analyses?.filter(a => 
    a.product_name.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight">
            Sevkiyat
          </h1>
          <p className="mt-2 text-muted-foreground">
            Depodan mağazaya ürün transferi yapın ve akıllı analiz görüntüleyin
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="transfer" className="gap-2">
              <Truck size={16} />
              Sevkiyat Yap
            </TabsTrigger>
            <TabsTrigger value="analysis" className="gap-2">
              <ChartLine size={16} />
              Akıllı Analiz
            </TabsTrigger>
          </TabsList>

          {/* Sevkiyat Tab */}
          <TabsContent value="transfer">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Warehouse Stock */}
              <div className="border border-border bg-card p-6 rounded-xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-secondary/10">
                    <Warehouse size={24} weight="duotone" className="text-secondary" />
                  </div>
                  <h2 className="font-heading text-xl font-semibold">Depo Stoku</h2>
                </div>
                <div className="space-y-3 max-h-[500px] overflow-auto">
                  {warehouseProducts.length === 0 ? (
                    <p className="text-muted-foreground text-sm py-8 text-center">
                      Depoda ürün bulunmuyor
                    </p>
                  ) : (
                    warehouseProducts.map((product) => (
                      <div
                        key={product.id}
                        className={`p-3 rounded-lg border transition-colors cursor-pointer ${
                          formData.product_id === product.id
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:bg-muted/50'
                        }`}
                        onClick={() => setFormData({ ...formData, product_id: product.id, quantity: '' })}
                        data-testid={`warehouse-product-${product.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-sm">{product.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {getCategoryLabel(product.category)}
                            </p>
                            {lastTransfers[product.id] && (
                              <p className="text-xs text-blue-500 mt-1 font-medium">
                                Son: {formatNumber(lastTransfers[product.id].quantity)} {product.unit} ({lastTransfers[product.id].date})
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="font-data font-semibold">
                              {formatNumber(product.warehouse_stock)}
                            </p>
                            <p className="text-xs text-muted-foreground">{product.unit}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Transfer Form with Smart Analysis */}
              <div className="border border-border bg-card p-6 rounded-xl">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Truck size={24} weight="duotone" className="text-primary" />
                  </div>
                  <h2 className="font-heading text-xl font-semibold">Sevkiyat Yap</h2>
                </div>

                {selectedProduct ? (
                  <form onSubmit={handleTransfer} className="space-y-4">
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <p className="font-medium">{selectedProduct.name}</p>
                      <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                        <div>
                          <span className="text-muted-foreground">Depo: </span>
                          <span className="font-data font-semibold">{formatNumber(selectedProduct.warehouse_stock)}</span>
                          <span className="text-muted-foreground"> {selectedProduct.unit}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Mağaza: </span>
                          <span className="font-data font-semibold">{formatNumber(selectedProduct.store_stock)}</span>
                          <span className="text-muted-foreground"> {selectedProduct.unit}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-center gap-4 py-2">
                      <div className="text-center">
                        <Warehouse size={28} weight="duotone" className="mx-auto text-secondary" />
                        <p className="text-xs text-muted-foreground mt-1">Depo</p>
                      </div>
                      <ArrowRight size={20} className="text-muted-foreground" />
                      <div className="text-center">
                        <Storefront size={28} weight="duotone" className="mx-auto text-primary" />
                        <p className="text-xs text-muted-foreground mt-1">Mağaza</p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                        Transfer Miktarı ({selectedProduct.unit})
                      </Label>
                      <Input
                        type="number"
                        step="0.01"
                        max={selectedProduct.warehouse_stock}
                        value={formData.quantity}
                        onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                        placeholder="0.00"
                        className="h-12 text-center text-lg font-data"
                        data-testid="transfer-quantity-input"
                      />
                    </div>

                    {/* Akıllı Analiz Önizleme */}
                    {previewLoading && (
                      <div className="p-3 bg-muted/30 rounded-lg text-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary mx-auto"></div>
                      </div>
                    )}

                    {transferPreview && !previewLoading && (
                      <div className={`p-4 rounded-lg border-2 ${deviationColors[transferPreview.deviation_level] || deviationColors.normal}`}>
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-xl">{transferPreview.deviation_emoji}</span>
                          <span className="font-semibold">{transferPreview.deviation_label}</span>
                        </div>
                        
                        {/* Öğrenme döneminde farklı görünüm */}
                        {transferPreview.is_learning_period ? (
                          <div className="space-y-2 text-sm">
                            <p className="text-blue-700">
                              Bu ürün için henüz yeterli sevkiyat geçmişi bulunmuyor.
                            </p>
                            <p className="text-blue-600 text-xs">
                              İlk sevkiyatlar öğrenme döneminde değerlendirilecektir.
                            </p>
                            <div className="grid grid-cols-2 gap-2 mt-3 pt-2 border-t border-blue-200">
                              <div>
                                <span className="text-muted-foreground">Mağaza Stok: </span>
                                <span className="font-data">{formatNumber(transferPreview.current_store_stock)} {transferPreview.unit}</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Depo Stok: </span>
                                <span className="font-data">{formatNumber(transferPreview.current_warehouse_stock)} {transferPreview.unit}</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Girilen: </span>
                                <span className="font-data font-semibold">{formatNumber(transferPreview.entered_transfer)} {transferPreview.unit}</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Geçmiş Sevkiyat: </span>
                                <span className="font-data">{transferPreview.transfer_count || 0} kayıt</span>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                              <div>
                                <span className="text-muted-foreground">Tahmini Günlük: </span>
                                <span className="font-data">{formatNumber(transferPreview.estimated_daily_consumption)} {transferPreview.unit}/gün</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Yeterlilik: </span>
                                <span className="font-data">
                                  {transferPreview.estimated_days_remaining 
                                    ? `${formatNumber(transferPreview.estimated_days_remaining)} gün` 
                                    : 'Hesaplanamadı'}
                                </span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Önerilen: </span>
                                <span className="font-data font-semibold">{formatNumber(transferPreview.recommended_transfer)} {transferPreview.unit}</span>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Girilen: </span>
                                <span className="font-data font-semibold">{formatNumber(transferPreview.entered_transfer)} {transferPreview.unit}</span>
                              </div>
                            </div>

                            <div className="flex items-center justify-between text-sm border-t pt-2 mt-2">
                              <span>Fark: <strong>{transferPreview.difference > 0 ? '+' : ''}{formatNumber(transferPreview.difference)} {transferPreview.unit}</strong></span>
                              <span>Sapma: <strong>{transferPreview.deviation_percent}%</strong></span>
                            </div>
                          </>
                        )}

                        {/* Güven Skoru */}
                        <div className={`mt-2 px-2 py-1 rounded text-xs inline-flex items-center gap-1 ${confidenceLabels[transferPreview.consumption_confidence]?.bg || 'bg-gray-100'}`}>
                          <Info size={12} />
                          <span className={confidenceLabels[transferPreview.consumption_confidence]?.color || 'text-gray-600'}>
                            {confidenceLabels[transferPreview.consumption_confidence]?.label || transferPreview.consumption_confidence}
                          </span>
                        </div>

                        {/* Veri kaynağı bilgisi */}
                        {transferPreview.data_source_label && (
                          <p className="text-xs text-muted-foreground mt-1">{transferPreview.data_source_label}</p>
                        )}

                        {transferPreview.confidence_reason && !transferPreview.is_learning_period && (
                          <p className="text-xs text-muted-foreground mt-2">{transferPreview.confidence_reason}</p>
                        )}

                        {transferPreview.warehouse_warning && (
                          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-xs flex items-center gap-2">
                            <Warning size={14} />
                            {transferPreview.warehouse_warning}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                        Not {transferPreview?.requires_explanation && <span className="text-red-500">*Zorunlu</span>}
                      </Label>
                      <Textarea
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        placeholder={transferPreview?.requires_explanation 
                          ? "Yüksek sapma nedeniyle açıklama zorunludur..." 
                          : "Sevkiyat notu..."}
                        className={`resize-none ${transferPreview?.requires_explanation ? 'border-orange-300 focus:border-orange-500' : ''}`}
                        rows={2}
                        data-testid="transfer-notes-input"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={transferring || !formData.quantity || (transferPreview?.requires_explanation && !formData.notes?.trim())}
                      className="w-full h-12 bg-primary hover:bg-primary/90"
                      data-testid="transfer-submit"
                    >
                      {transferring ? (
                        'Transfer ediliyor...'
                      ) : (
                        <>
                          <Truck size={20} className="mr-2" />
                          Sevkiyatı Tamamla
                        </>
                      )}
                    </Button>
                  </form>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Package size={48} weight="duotone" className="text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground">
                      Soldaki listeden transfer etmek istediğiniz ürünü seçin
                    </p>
                  </div>
                )}
              </div>

              {/* Store Stock */}
              <div className="border border-border bg-card p-6 rounded-xl">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Storefront size={24} weight="duotone" className="text-primary" />
                  </div>
                  <h2 className="font-heading text-xl font-semibold">Mağaza Stoku</h2>
                </div>
                <div className="space-y-3 max-h-[500px] overflow-auto">
                  {products.filter(p => p.store_stock > 0).length === 0 ? (
                    <p className="text-muted-foreground text-sm py-8 text-center">
                      Mağazada ürün bulunmuyor
                    </p>
                  ) : (
                    products
                      .filter(p => p.store_stock > 0)
                      .map((product) => (
                        <div
                          key={product.id}
                          className="p-3 rounded-lg border border-border"
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-sm">{product.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {getCategoryLabel(product.category)}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-data font-semibold">
                                {formatNumber(product.store_stock)}
                              </p>
                              <p className="text-xs text-muted-foreground">{product.unit}</p>
                            </div>
                          </div>
                        </div>
                      ))
                  )}
                </div>
              </div>
            </div>

            {/* Recent Transfers */}
            {transactions.length > 0 && (
              <div className="border border-border bg-card p-6 rounded-xl mt-6">
                <h3 className="font-heading text-lg font-semibold mb-4">Son Sevkiyatlar</h3>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Ürün</TableHead>
                        <TableHead className="text-right">Miktar</TableHead>
                        <TableHead>Not</TableHead>
                        <TableHead>Tarih</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transactions.slice(0, 10).map((t) => (
                        <TableRow key={t.id}>
                          <TableCell className="font-medium">{t.product_name}</TableCell>
                          <TableCell className="text-right font-data">
                            {formatNumber(t.quantity)}
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm max-w-[200px] truncate">
                            {t.notes || '-'}
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {t.created_at?.slice(0, 10)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Akıllı Analiz Tab */}
          <TabsContent value="analysis">
            {analysisLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Özet Kartlar */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  <div className="border border-border bg-card p-4 rounded-xl text-center">
                    <p className="text-2xl font-bold text-primary">{smartAnalysis?.summary?.total || 0}</p>
                    <p className="text-xs text-muted-foreground">Toplam Ürün</p>
                  </div>
                  <div className="border border-red-200 bg-red-50 p-4 rounded-xl text-center">
                    <p className="text-2xl font-bold text-red-600">{smartAnalysis?.summary?.critical || 0}</p>
                    <p className="text-xs text-red-600">🔴 Kritik</p>
                  </div>
                  <div className="border border-orange-200 bg-orange-50 p-4 rounded-xl text-center">
                    <p className="text-2xl font-bold text-orange-600">{smartAnalysis?.summary?.high_deviation || 0}</p>
                    <p className="text-xs text-orange-600">🟠 Yüksek Sapma</p>
                  </div>
                  <div className="border border-yellow-200 bg-yellow-50 p-4 rounded-xl text-center">
                    <p className="text-2xl font-bold text-yellow-600">{smartAnalysis?.summary?.attention || 0}</p>
                    <p className="text-xs text-yellow-600">🟡 Dikkat</p>
                  </div>
                  <div className="border border-green-200 bg-green-50 p-4 rounded-xl text-center">
                    <p className="text-2xl font-bold text-green-600">{smartAnalysis?.summary?.normal || 0}</p>
                    <p className="text-xs text-green-600">✅ Normal</p>
                  </div>
                  <div className="border border-gray-200 bg-gray-50 p-4 rounded-xl text-center">
                    <p className="text-2xl font-bold text-gray-600">{smartAnalysis?.summary?.low_confidence || 0}</p>
                    <p className="text-xs text-gray-600">⚠️ Düşük Güven</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* En Çok Sevk Edilen */}
                  <div className="border border-border bg-card p-4 rounded-xl">
                    <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                      <TrendUp size={18} className="text-primary" />
                      En Çok Sevk Edilen (30 Gün)
                    </h3>
                    <div className="space-y-2">
                      {topTransfers.slice(0, 5).map((t, i) => (
                        <div key={t.product_id} className="flex items-center justify-between text-sm p-2 bg-muted/30 rounded">
                          <span className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">{i + 1}.</span>
                            <span className="font-medium truncate max-w-[150px]">{t.product_name}</span>
                          </span>
                          <span className="font-data text-primary">{formatNumber(t.total_quantity)} {t.unit}</span>
                        </div>
                      ))}
                      {topTransfers.length === 0 && (
                        <p className="text-sm text-muted-foreground text-center py-4">Veri yok</p>
                      )}
                    </div>
                  </div>

                  {/* Eğilim Özeti */}
                  <div className="border border-border bg-card p-4 rounded-xl">
                    <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                      <ChartLine size={18} className="text-secondary" />
                      Sevkiyat Eğilimi
                    </h3>
                    {trends?.summary && (
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-2">
                          <div className="p-3 bg-blue-50 rounded-lg text-center">
                            <p className="text-lg font-bold text-blue-600">{formatNumber(trends.summary.total_7_day)}</p>
                            <p className="text-xs text-blue-600">Son 7 Gün</p>
                          </div>
                          <div className="p-3 bg-purple-50 rounded-lg text-center">
                            <p className="text-lg font-bold text-purple-600">{formatNumber(trends.summary.total_30_day)}</p>
                            <p className="text-xs text-purple-600">Son 30 Gün</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Günlük Ort (7g):</span>
                            <span className="font-data">{formatNumber(trends.summary.avg_daily_7)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Günlük Ort (30g):</span>
                            <span className="font-data">{formatNumber(trends.summary.avg_daily_30)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Sevkiyat (7g):</span>
                            <span className="font-data">{trends.summary.transfer_count_7} adet</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Sevkiyat (30g):</span>
                            <span className="font-data">{trends.summary.transfer_count_30} adet</span>
                          </div>
                        </div>
                        {/* Mini Grafik (Basit Bar Chart) */}
                        <div className="mt-3 pt-3 border-t">
                          <p className="text-xs text-muted-foreground mb-2">Son 14 Gün</p>
                          <div className="flex items-end gap-1 h-12">
                            {trends.daily_data?.slice(-14).map((d, i) => {
                              const maxVal = Math.max(...trends.daily_data.slice(-14).map(x => x.total_quantity)) || 1;
                              const height = (d.total_quantity / maxVal) * 100;
                              return (
                                <div
                                  key={i}
                                  className="flex-1 bg-primary/60 hover:bg-primary rounded-t transition-all"
                                  style={{ height: `${Math.max(height, 2)}%` }}
                                  title={`${d.date}: ${formatNumber(d.total_quantity)}`}
                                />
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Son Sapmalar */}
                  <div className="border border-border bg-card p-4 rounded-xl">
                    <h3 className="font-heading font-semibold mb-3 flex items-center gap-2">
                      <Warning size={18} className="text-orange-500" />
                      Son Sapmalar (30 Gün)
                    </h3>
                    <div className="space-y-2 max-h-[200px] overflow-auto">
                      {deviations?.deviations?.slice(0, 5).map((d) => (
                        <div key={d.transaction_id} className="text-sm p-2 bg-muted/30 rounded">
                          <div className="flex items-center justify-between">
                            <span className="font-medium truncate max-w-[120px]">{d.product_name}</span>
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              d.deviation_type === 'excess' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
                            }`}>
                              {d.deviation_emoji} {d.deviation_type === 'excess' ? 'Fazla' : 'Eksik'}
                            </span>
                          </div>
                          <div className="flex justify-between text-xs text-muted-foreground mt-1">
                            <span>{d.date}</span>
                            <span>%{formatNumber(d.deviation_percent)} sapma</span>
                          </div>
                        </div>
                      ))}
                      {(!deviations?.deviations || deviations.deviations.length === 0) && (
                        <p className="text-sm text-muted-foreground text-center py-4">Sapma tespit edilmedi</p>
                      )}
                    </div>
                    {deviations?.summary && (
                      <div className="mt-3 pt-3 border-t grid grid-cols-2 gap-2 text-xs">
                        <div className="text-center">
                          <span className="text-orange-600 font-semibold">{deviations.summary.excess_count}</span>
                          <span className="text-muted-foreground"> Fazla</span>
                        </div>
                        <div className="text-center">
                          <span className="text-blue-600 font-semibold">{deviations.summary.shortage_count}</span>
                          <span className="text-muted-foreground"> Eksik</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Ürün Listesi */}
                <div className="border border-border bg-card p-4 rounded-xl">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-heading font-semibold">Tüm Ürünler - Akıllı Analiz</h3>
                    <div className="relative">
                      <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input
                        placeholder="Ürün ara..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9 w-64"
                      />
                    </div>
                  </div>
                  
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Ürün</TableHead>
                          <TableHead className="text-right">Mağaza</TableHead>
                          <TableHead className="text-right">Depo</TableHead>
                          <TableHead className="text-right">Günlük Tüketim</TableHead>
                          <TableHead className="text-right">Yeterlilik</TableHead>
                          <TableHead className="text-right">Önerilen</TableHead>
                          <TableHead>Güven</TableHead>
                          <TableHead>Durum</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredAnalysis.slice(0, 50).map((a) => (
                          <TableRow key={a.product_id}>
                            <TableCell className="font-medium">{a.product_name}</TableCell>
                            <TableCell className="text-right font-data">
                              {formatNumber(a.current_store_stock)} {a.unit}
                            </TableCell>
                            <TableCell className="text-right font-data">
                              {formatNumber(a.current_warehouse_stock)} {a.unit}
                            </TableCell>
                            <TableCell className="text-right font-data">
                              {formatNumber(a.estimated_daily_consumption)} {a.unit}/gün
                            </TableCell>
                            <TableCell className="text-right font-data">
                              {a.estimated_days_remaining ? `${formatNumber(a.estimated_days_remaining)} gün` : '-'}
                            </TableCell>
                            <TableCell className="text-right font-data font-semibold">
                              {formatNumber(a.recommended_transfer)} {a.unit}
                            </TableCell>
                            <TableCell>
                              <span className={`text-xs px-2 py-1 rounded ${confidenceLabels[a.consumption_confidence]?.bg} ${confidenceLabels[a.consumption_confidence]?.color}`}>
                                {confidenceLabels[a.consumption_confidence]?.label}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className={`text-xs px-2 py-1 rounded whitespace-nowrap ${
                                a.status === 'critical' ? 'bg-red-100 text-red-700' :
                                a.status === 'high_deviation' ? 'bg-orange-100 text-orange-700' :
                                a.status === 'attention' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-green-100 text-green-700'
                              }`}>
                                {a.status_label?.split(' - ')[0]}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {filteredAnalysis.length > 50 && (
                      <p className="text-sm text-muted-foreground text-center py-2">
                        {filteredAnalysis.length - 50} ürün daha var...
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
